import { ReactElement, useLayoutEffect, useState } from 'react';
import { Component as MenuIcon } from 'icons/icon-ui--menu.svg';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import { Component as BookIcon } from 'icons/icon-ui--book.svg';
import { Component as ContactUsIcon } from 'icons/icon-ui--contact-us.svg';
import { Component as FAQIcon } from 'icons/icon-ui--faqs.svg';
import { Component as ViewAllIcon } from 'icons/icon-ui--forkspoon.svg';
import { Component as GiftIcon } from 'icons/icon-ui--gift.svg';
import { Component as PackageIcon } from 'icons/icon-ui--package.svg';
import { Component as PlantIcon } from 'icons/icon-ui--plant.svg';
import { Component as SquaresIcon } from 'icons/icon-ui--squares.svg';
import { MenuSection } from '../menu-section/MenuSection';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { SignupStep } from '../../constants/signup';
import Link from '../shared/link/Link';
import { Drawer } from '../shared/drawer/Drawer';
import { Button } from '../shared/button/Button';
import { Route } from '../../utils/config';
import styles from './Header.module.scss';

type Props = {
  setSignInModalVisible?: (val: boolean) => void;
}

export function HeaderSidebar({ setSignInModalVisible = undefined }: Props): ReactElement {
  const { user } = useUser();
  const { isLoggedIn } = useAuth();
  const { currentSignupStep } = useMenuWithCart();
  const isEmployee = user?.isEmployee;
  const showLoginButton = (!isLoggedIn && !currentSignupStep)
    || currentSignupStep === SignupStep.CREATE_ACCOUNT;
  const showLogoutButton = currentSignupStep && currentSignupStep !== SignupStep.CREATE_ACCOUNT;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleCloseSidebar = () => setIsSidebarOpen(false);
  const [scrollToBottomVal, setScrollScrollToBottomToBottom] = useState<boolean>(false);

  const scrollToBottom = () => {
    const body = document.getElementById(
      'cart-scroll-area',
    ) as HTMLDivElement;

    body.scrollTo({
      top: body.scrollHeight,
      behavior: 'smooth',
    });
  };

  useLayoutEffect(() => {
    if (!scrollToBottomVal) return;

    scrollToBottom();
  }, [scrollToBottomVal]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={styles.sidebar_button}
      >
        {isSidebarOpen ? (
          <CloseIcon
            width="23"
            focusable="false"
          />
        ) : (
          <MenuIcon
            width="23"
            focusable="false"
          />
        )}
      </button>

      <Drawer
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        position="left"
        offsetTop="61px"
        zIndex={5}
        trapFocus={false}
        size="sm"
        content={(
          <div
            id="cart-scroll-area"
            className={styles.scroll_area}
          >
            <nav aria-label="Site navigation" className={styles.sidebar_container}>
              <ul className={styles.sidebar_nav_list}>
                {isLoggedIn ? (
                  <Link
                    href={Route.HUB}
                    data-cy="offcanvas-upcoming-deliveries"
                    onClick={handleCloseSidebar}
                    className={({ isActive }) => ([
                      'c-nav-header__sublink',
                      isActive ? 'is-active' : '',
                    ].join(' '))}
                  >
                    <div className={styles.sidebar_link_content}>
                      <ViewAllIcon width="12px" fill="#000" />
                      Weekly Menu
                    </div>
                  </Link>
                ) : (
                  <Link
                    href={Route.SHOP}
                    data-cy="offcanvas-shop"
                    onClick={handleCloseSidebar}
                    className={({ isActive }) => ([
                      'c-nav-header__sublink',
                      isActive ? 'is-active' : '',
                    ].join(' '))}
                  >
                    <div className={styles.sidebar_link_content}>
                      <ViewAllIcon width="12px" fill="#000" />
                      Shop
                    </div>
                  </Link>
                )}

              </ul>
              <li className={styles.sidebar_divider} />
              <ul className={styles.sidebar_nav_list}>
                {isLoggedIn && (
                  <Link
                    href={Route.ALL_RECIPES}
                    onModalClose={handleCloseSidebar}
                    data-cy="offcanvas-view-all-recipes"
                    className={({ isActive }) => ([
                      'c-nav-header__sublink',
                      isActive ? 'is-active' : '',
                    ].join(' '))}
                    reload
                  >
                    <div className={styles.sidebar_link_content}>
                      <BookIcon />
                      All Recipes
                    </div>
                  </Link>
                )}
                <Link
                  href={Route.WHY_PLANTS}
                  onModalClose={handleCloseSidebar}
                  data-cy="offcanvas-why-plants"
                  className={({ isActive }) => ([
                    'c-nav-header__sublink',
                    isActive ? 'is-active' : '',
                  ].join(' '))}
                  reload
                >
                  <div className={styles.sidebar_link_content}>
                    <PlantIcon />
                    Why Plants
                  </div>
                </Link>
                {!isEmployee && !isLoggedIn && (
                  <Link
                    href={Route.GIFTS}
                    onModalClose={handleCloseSidebar}
                    data-cy="offcanvas-gifts"
                    className={({ isActive }) => ([
                      'c-nav-header__sublink',
                      isActive ? 'is-active' : '',
                    ].join(' '))}
                    reload
                  >
                    <div className={styles.sidebar_link_content}>
                      <GiftIcon />
                      Gifts
                    </div>
                  </Link>
                )}

              </ul>
              <li className={styles.sidebar_divider} />
              {isLoggedIn && (
                <>
                  <ul className={styles.sidebar_nav_list}>
                    <Link
                      href={Route.ORDER_HISTORY}
                      data-cy="offcanvas-order-history"
                      onClick={handleCloseSidebar}
                      onModalClose={handleCloseSidebar}
                      className={({ isActive }) => ([
                        'c-nav-header__sublink',
                        isActive ? 'is-active' : '',
                      ].join(' '))}
                    >
                      <div className={styles.sidebar_link_content}>
                        <SquaresIcon />
                        Order History
                      </div>
                    </Link>
                    <Link
                      href={Route.TRACKING}
                      data-cy="offcanvas-track-package"
                      onClick={handleCloseSidebar}
                      onModalClose={handleCloseSidebar}
                      className={({ isActive }) => ([
                        'c-nav-header__sublink',
                        isActive ? 'is-active' : '',
                      ].join(' '))}
                    >
                      <div className={styles.sidebar_link_content}>
                        <PackageIcon />
                        Track Package
                      </div>
                    </Link>
                  </ul>
                  <li className={styles.sidebar_divider} />
                </>
              )}

              <ul className={styles.sidebar_nav_list}>
                <Link
                  href={Route.FAQS}
                  onModalClose={handleCloseSidebar}
                  data-cy="offcanvas-faqs"
                  className={({ isActive }) => ([
                    'c-nav-header__sublink',
                    isActive ? 'is-active' : '',
                  ].join(' '))}
                  reload
                >
                  <div className={styles.sidebar_link_content}>
                    <FAQIcon />
                    <abbr>
                      FAQs
                    </abbr>
                  </div>
                </Link>
                <Link
                  href={Route.CONTACT_US}
                  onModalClose={handleCloseSidebar}
                  data-cy="offcanvas-contact-us"
                  className={({ isActive }) => ([
                    'c-nav-header__sublink',
                    isActive ? 'is-active' : '',
                  ].join(' '))}
                  reload
                >
                  <div className={styles.sidebar_link_content}>
                    <ContactUsIcon />
                    Contact Us
                  </div>
                </Link>
              </ul>
              <li className={styles.sidebar_divider} />
              {isLoggedIn && (
                <MenuSection
                  isInHamburger
                  onClose={() => setIsSidebarOpen(false)}
                />
              )}
              {showLoginButton && (
                <Button
                  text="Log In"
                  className={styles.sidebar_login_button}
                  onClick={() => {
                    if (setSignInModalVisible) {
                      setSignInModalVisible(true);
                    }
                  }}
                />
              )}

              {showLogoutButton && (
                <form
                  method="POST"
                  action="/users/sign_out"
                  onSubmit={() => {
                    document.cookie = `account_creation_email=; path=/; expires=${new Date(
                      0,
                    ).toUTCString()}`;
                  }}
                >
                  <input type="hidden" name="_method" value="delete" />
                  <input
                    className={styles.sidebar_login_button}
                    type="submit"
                    value="Log Out"
                  />
                </form>
              )}
            </nav>
          </div>
        )}
      />
    </>
  );
}
